import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";

import { CustomModalButtons } from "@constants/global.constants";

import { ExclamationIconRound } from "@components/SVGs";
import WPButton from "@components/WPButton";
import WPHeading from "@components/WPHeading";
import WPLoader from "@components/WPLoader";
import { WPTypogrpahy } from "@components/WPTypography";

import CheckCircleIcon from "@common/icons/CheckCircleIcon";
import InfoTealIcon from "@common/icons/InfoTealIcon";
import WarningIcon from "@common/icons/WarningIcon";
import StyledModal from "@common/styles/components/modals/CustomModal";

import "./index.css";

export const DIALOG_TYPES = {
  ERROR: "danger",
  WARNING: "warning",
  PRIMARY: "primary",
  INFO: "info",
};

const ICONS = {
  [DIALOG_TYPES.WARNING]: <WarningIcon />,
  [DIALOG_TYPES.ERROR]: <ExclamationIconRound />,
  [DIALOG_TYPES.PRIMARY]: <CheckCircleIcon />,
  [DIALOG_TYPES.INFO]: <InfoTealIcon />,
};

/** Resolve WPButton type for dialog types that have no direct button variant. */
const resolveButtonType = type => (type === DIALOG_TYPES.INFO ? DIALOG_TYPES.PRIMARY : type);

/**
 * ConfirmationPopup is a reusable modal component that displays a confirmation dialog.
 * It shows a header, an optional description, and buttons for confirming or canceling an action.
 * The `dangerouslySetInnerHTML` attribute is used to set the inner HTML of the description.
 * DOMPurify is used to sanitize the HTML content to prevent XSS attacks.
 */
const ConfirmationPopup = ({
  header,
  type,
  onClose,
  description,
  confirmBtn,
  cancelBtn,
  linkBtn,
  modalClassName,
  wrapContent = false,
  animation = true,
  isLoading = false,
  loadingMsg,
  maxWidth = "420px",
}) => {
  const cancelBtnRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const linkBtnRef = useRef(null);

  useEffect(() => {
    if (confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }, []);

  const handleClick = ({ target }) => onClose(target.id);

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      /* I added this because some of the modals not getting closed when clicked "Enter" 
      due to presence of any form elements in the wrapper modal that might be causing
       the Enter key to submit the form instead of triggering the handler */
      event.preventDefault();
      handleClick(event);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <StyledModal
      show
      centered
      onHide={onClose}
      backdrop='static'
      maxWidth={maxWidth}
      className={modalClassName ? `confirmation-popup ${modalClassName}` : "confirmation-popup"}
      animation={animation}
    >
      {isLoading && <WPLoader loadingMsg={loadingMsg} />}
      <Modal.Body>
        <div className='message-pop-up'>
          <div className='icon-container'>{ICONS[type]}</div>
          <div className='message-margin'>
            <WPHeading text={header} type='default' wrapContent={wrapContent} />
            {description && <WPTypogrpahy className='description-container fs-16' text={description} renderHtml />}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {linkBtn && (
          <WPButton
            variant='text'
            type={resolveButtonType(type)}
            id={CustomModalButtons.CLOSE_BTN}
            onClick={handleClick}
            ref={linkBtnRef} // Attach the ref to the close button
          >
            {linkBtn}
          </WPButton>
        )}
        {cancelBtn && (
          <WPButton
            variant='outlined'
            type={resolveButtonType(type)}
            id={CustomModalButtons.CANCEL}
            onClick={handleClick}
            ref={cancelBtnRef} // Attach the ref to the cancel button
          >
            {cancelBtn}
          </WPButton>
        )}
        {confirmBtn && (
          <WPButton
            type={resolveButtonType(type)}
            id={CustomModalButtons.CONFIRM}
            onClick={handleClick}
            ref={confirmBtnRef} // Attach the ref to the confirm button
          >
            {confirmBtn}
          </WPButton>
        )}
      </Modal.Footer>
    </StyledModal>
  );
};

ConfirmationPopup.propTypes = {
  header: PropTypes.string,
  type: PropTypes.oneOf(Object.keys(ICONS)),
  onClose: PropTypes.func.isRequired,
  description: PropTypes.string,
  confirmBtn: PropTypes.string,
  cancelBtn: PropTypes.string,
  modalClassName: PropTypes.string,
  wrapContent: PropTypes.bool,
  animation: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadingMsg: PropTypes.string,
};

export default ConfirmationPopup;
